import {
  RELATIVE_WINDOWS,
  type RelativeWindowKey,
} from "@/constants/date-presets"
import {
  differenceInDays,
  endOfDay,
  endOfYesterday,
  isAfter,
  isValid,
  startOfDay,
  startOfToday,
  startOfYesterday,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns"

import type { DynamicDateRange } from "@/components/date-range/DynamicDateRangeInput"
import {
  DATE_RANGE_PRESET,
  type CustomDateRange,
  type DateRange,
  type DateRangePreset,
  type DateRangeUnit,
} from "@/utils/date/dateRangeTypes"

/**
 * The rolling window behind each preset key the dashboards persist.
 *
 * The keys are stored on saved dashboards, so they stay as they are; what each
 * one spans comes from the app's window registry, so a dashboard's "Last 7
 * days" covers the same time as the same preset on any other surface.
 */
const PRESET_WINDOWS = {
  [DATE_RANGE_PRESET.LAST_1_DAY]: "1d",
  [DATE_RANGE_PRESET.LAST_3_DAYS]: "3d",
  [DATE_RANGE_PRESET.LAST_7_DAYS]: "7d",
  [DATE_RANGE_PRESET.LAST_14_DAYS]: "14d",
  [DATE_RANGE_PRESET.LAST_30_DAYS]: "30d",
} as const satisfies Partial<Record<DateRangePreset, RelativeWindowKey>>

/** The presets that name a day rather than a window, and the customs. */
const STANDALONE_PRESET_LABELS: Record<string, string> = {
  [DATE_RANGE_PRESET.TODAY]: "Today",
  [DATE_RANGE_PRESET.YESTERDAY]: "Yesterday",
  [DATE_RANGE_PRESET.CUSTOM_FIXED]: "Custom fixed range",
  [DATE_RANGE_PRESET.CUSTOM_RELATIVE]: "In the last N days/weeks/months",
  [DATE_RANGE_PRESET.CUSTOM_UNTIL_NOW]: "From custom date until now",
}

/**
 * How a preset key reads in the UI, on the wording the registry defines, so no
 * two surfaces label the same window differently.
 */
export function getPresetLabel(preset: DateRangePreset): string {
  const window = PRESET_WINDOWS[preset as keyof typeof PRESET_WINDOWS]
  if (window) {
    return RELATIVE_WINDOWS[window].label
  }
  return STANDALONE_PRESET_LABELS[preset] ?? RELATIVE_WINDOWS["7d"].label
}

/**
 * The range a preset key resolves to, recalculated on every read so a saved
 * "Last 7 days" tracks rather than freezing at the week it was saved in.
 */
export function calculatePresetDateRange(preset: DateRangePreset): DateRange {
  const window = PRESET_WINDOWS[preset as keyof typeof PRESET_WINDOWS]
  if (window) {
    const now = new Date()
    return {
      start: new Date(now.getTime() - RELATIVE_WINDOWS[window].durationMs),
      end: now,
    }
  }

  if (preset === DATE_RANGE_PRESET.TODAY) {
    return {
      start: startOfToday(),
      end: endOfDay(new Date()),
    }
  }

  // Yesterday, and every key with no range of its own: a custom range that
  // never reached the handling that supplies its dates, or a key from a build
  // this one does not know. Yesterday is a whole day of real data either way.
  return {
    start: startOfYesterday(),
    end: endOfYesterday(),
  }
}

/**
 * Calculate dynamic date range using precise date-fns functions
 */
export function calculateDynamicDateRange(
  amount: number,
  unit: DateRangeUnit,
): DateRange {
  const now = new Date()
  let startDate: Date

  switch (unit) {
    case "days":
      startDate = startOfDay(subDays(now, amount - 1)) // Include today
      break
    case "weeks":
      startDate = startOfDay(subWeeks(now, amount))
      break
    case "months":
      startDate = startOfDay(subMonths(now, amount))
      break
    default:
      startDate = startOfDay(subDays(now, amount - 1))
  }

  return {
    start: startDate,
    end: now, // Current time, not end of day
  }
}

/**
 * Calculate "from custom date until now" range
 */
function calculateFromDateUntilNow(startDate: Date): DateRange {
  return {
    start: startOfDay(startDate),
    end: new Date(), // Current time
  }
}

/**
 * Calculate custom fixed date range
 */
function calculateCustomFixedRange(startDate: Date, endDate: Date): DateRange {
  return {
    start: startOfDay(startDate),
    end: endOfDay(endDate),
  }
}

/**
 * Convert CustomDateRange to DateRange
 */
export function customDateRangeToDateRange(
  customRange: CustomDateRange,
): DateRange {
  if (customRange.type === "from-date-until-now") {
    return calculateFromDateUntilNow(customRange.startDate)
  }
  return calculateCustomFixedRange(customRange.startDate, customRange.endDate!)
}

/**
 * Convert DynamicDateRange to DateRange
 */
export function dynamicDateRangeToDateRange(
  dynamicRange: DynamicDateRange,
): DateRange {
  return calculateDynamicDateRange(dynamicRange.value, dynamicRange.unit)
}

/**
 * Validate custom date range inputs
 */
export function validateCustomDateRange(
  startDate: Date,
  endDate?: Date,
  type: "fixed" | "from-date-until-now" = "fixed",
): {
  isValid: boolean
  error?: string
} {
  if (!isValid(startDate)) {
    return {
      isValid: false,
      error: "Invalid start date",
    }
  }

  if (isAfter(startOfDay(startDate), startOfToday())) {
    return {
      isValid: false,
      error: "Start date cannot be in the future",
    }
  }

  // For fixed ranges, validate end date
  if (type === "fixed") {
    if (!endDate || !isValid(endDate)) {
      return {
        isValid: false,
        error: "Invalid end date",
      }
    }

    if (isAfter(startDate, endDate)) {
      return {
        isValid: false,
        error: "Start date must be before end date",
      }
    }

    if (isAfter(startOfDay(endDate), startOfToday())) {
      return {
        isValid: false,
        error: "End date cannot be in the future",
      }
    }

    // Check if date range is too long (e.g., more than 2 years)
    const daysDifference = differenceInDays(endDate, startDate)
    if (daysDifference > 730) {
      // 2 years
      return {
        isValid: false,
        error: "Date range cannot exceed 2 years",
      }
    }
  } else {
    const now = new Date()

    // The window ends at the current time, so a start later today inverts it.
    // The day-level check above passes for a time still ahead on today's date.
    if (isAfter(startDate, now)) {
      return {
        isValid: false,
        error: "Start date cannot be in the future",
      }
    }

    // For "from-date-until-now", check if start date is too far in the past
    const daysSinceStart = differenceInDays(now, startDate)
    if (daysSinceStart > 730) {
      // 2 years
      return {
        isValid: false,
        error: "Start date cannot be more than 2 years in the past",
      }
    }
  }

  return {
    isValid: true,
  }
}

/**
 * Validate dynamic date range inputs
 */
export function validateDynamicDateRange(
  value: number,
  unit: DateRangeUnit,
): {
  isValid: boolean
  error?: string
} {
  if (!Number.isInteger(value) || value <= 0) {
    return {
      isValid: false,
      error: "Value must be a positive integer",
    }
  }

  // Set reasonable limits based on unit
  let maxValue: number
  switch (unit) {
    case "days":
      maxValue = 730 // 2 years
      break
    case "weeks":
      maxValue = 104 // 2 years
      break
    case "months":
      maxValue = 24 // 2 years
      break
    default:
      maxValue = 730
  }

  if (value > maxValue) {
    return {
      isValid: false,
      error: `Value cannot exceed ${maxValue} ${unit}`,
    }
  }

  return {
    isValid: true,
  }
}
